import mandatorUtils from "utils/mandators";
import { safeParse } from "interfaces/schemas/productListSchema";
import type {
  ProductListLinkNode,
  ProductListRootNode,
  ProductListNode,
  ProductListLeafNode,
} from "interfaces/schemas/productListSchema";

export const filterProductListByMandator = (
  productList: ProductListRootNode[],
  mandatorKey: number | string,
  tenantKey: string,
) => {
  const filterByCountryCodes = createMandatorAndTenantFilter(
    mandatorKey,
    tenantKey,
  );
  const filterByCountryNames =
    mandatorUtils.getCountryNamesByCountryCodes(filterByCountryCodes);
  return filterManyNodesByCountryOfOrigin(productList, filterByCountryNames);
};

const createMandatorAndTenantFilter = (
  mandatorKey: number | string,
  tenantKey: string,
) => {
  try {
    const mandator = mandatorUtils.parseMandatorKey(mandatorKey);
    const tenant = mandatorUtils.parseTenant(tenantKey);
    const countryCodes = mandatorUtils.getCountryCodesByMandatorKey(mandator);
    const businessPartners = mandatorUtils.getBusinessPartnersByTenant(tenant);
    return mandatorUtils.maskCountryCodesByBusinessPartners(
      countryCodes,
      businessPartners,
    );
  } catch (error) {
    const typeSafeError =
      error instanceof Error ? error : new Error(String(error));
    console.error(
      `An Error occured while getting productList filters: ${typeSafeError.message}`,
    );
    return [];
  }
};

const filterManyNodesByCountryOfOrigin = (
  nodes: ProductListNode[],
  filter: string[] = [],
) => {
  const filteredNodes: ProductListNode[] = [];
  nodes.forEach((node) => {
    const filteredNode = filterNodeByCountryOfOrigin(node, filter);
    if (filteredNode !== null) {
      filteredNodes.push(filteredNode);
    }
  });
  return filteredNodes;
};

const filterNodeByCountryOfOrigin = (
  node: ProductListNode,
  filter: string[] = [],
) => {
  const { data: leafNode, error: leafError } = safeParse.leaf(node);
  if (!leafError) {
    if (filter.includes(leafNode.countryOfOrigin)) {
      return leafNode;
    }
    return null;
  }
  const { data: linkNode, error: linkError } = safeParse.link(node);
  const { data: rootNode, error: rootError } = safeParse.root(node);
  if (!linkError) {
    return filterLinkOrRoot(linkNode, filter);
  }
  if (!rootError) {
    return filterLinkOrRoot(rootNode, filter);
  }

  return null;
};

const filterLinkOrRoot = (
  node: ProductListRootNode | ProductListLinkNode,
  filter: string[] = [],
) => {
  const filteredChildren: ProductListNode[] = [];
  node.nodes.forEach((child) => {
    const filteredChild = filterNodeByCountryOfOrigin(child, filter);
    if (filteredChild !== null) {
      filteredChildren.push(filteredChild);
    }
  });
  if (filteredChildren.length > 0) {
    // strip out nodes to avoid creating a deep copy of them.
    const { nodes: _nodes, ...rest } = node;
    const nodeDeepClone = structuredClone(rest);
    return {
      ...nodeDeepClone,
      nodes: filteredChildren,
    };
  }
  return null;
};

export const getMandatorsByProduct = (
  allProducts: ProductListRootNode[],
  seriesId: string | number,
  productName: string,
  mandatorOptions: Array<{ value: number; label: string }>,
  currentUserTenant: string,
) => {
  const product = findProductInTree(allProducts, seriesId, productName);

  if (!product || !product.countryOfOrigin) {
    return [];
  }

  const validMandators = mandatorOptions.filter((option) => {
    const countryCodes = createMandatorAndTenantFilter(
      option.value,
      currentUserTenant,
    );
    const countryNames =
      mandatorUtils.getCountryNamesByCountryCodes(countryCodes);

    return countryNames.includes(product.countryOfOrigin);
  });

  return validMandators;
};

export const findProductInTree = (
  nodes: ProductListNode[],
  seriesId: string | number,
  productName: string,
): ProductListLeafNode | null => {
  for (const node of nodes) {
    const { data: leafNode, error: leafError } = safeParse.leaf(node);

    if (!leafError && leafNode) {
      const matchesSeriesId =
        leafNode.serieId === seriesId ||
        String(leafNode.serieId) === String(seriesId);
      const matchesName = leafNode.name === productName;

      if (matchesSeriesId && matchesName) {
        return leafNode;
      }
    }

    const { data: linkNode } = safeParse.link(node);
    const { data: rootNode } = safeParse.root(node);

    const parentNode = linkNode || rootNode;
    if (parentNode && parentNode.nodes) {
      const found = findProductInTree(parentNode.nodes, seriesId, productName);
      if (found) {
        return found;
      }
    }
  }

  return null;
};
